import _, {isEqual, mapValues} from 'lodash';
import {
  createSelector,
  createSelectorCreator,
  createStructuredSelector as reselectCreateStructuredSelector,
  defaultMemoize,
  ParametricSelector,
  Selector,
} from 'reselect';

import {AppState} from '../store/appState';
import {Dispatch} from '../store/appStore';
import {ThunkAction} from '../store/appThunk';

/*
 * Reselect.
 */

export type AppSelector<T> = Selector<AppState, T>;
export type AppParametricSelector<P, T> = ParametricSelector<AppState, P, T>;

/** Selector creator that deeply compares arguments. */
export const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

/** Selector that shallowly compares arguments. */
export const createShallowEqualSelector = createSelectorCreator(defaultMemoize, isShallowEqual);

/** Selector that uses the default/strict comparison on all arguments except the first. */
export const createTailStrictEqualSelector = createSelectorCreator(tailMemoize);

/** Wrap the output of a selector in a new deep equal selector. */
export function wrapInDeepEqualSelector<R>(selector: AppSelector<R>): AppSelector<R>;
export function wrapInDeepEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
): AppParametricSelector<P, R>;
export function wrapInDeepEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
): AppParametricSelector<P, R> {
  return createDeepEqualSelector(selector, (value) => value);
}

/** Wrap the output of a selector in a new shallow selector. */
export function wrapInShallowSelector<R>(selector: AppSelector<R>): AppSelector<R>;
export function wrapInShallowSelector<P, R>(
  selector: AppParametricSelector<P, R>,
): AppParametricSelector<P, R>;
export function wrapInShallowSelector<P, R>(
  selector: AppParametricSelector<P, R>,
): AppParametricSelector<P, R> {
  return createShallowEqualSelector(selector, (value) => value);
}

/** Wrap the output of a selector in a new selector with a custom equal function. */
export function wrapInCustomEqualSelector<R>(
  selector: AppSelector<R>,
  customIsEqual: (value: any, other: any) => boolean,
): AppSelector<R>;
export function wrapInCustomEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  customIsEqual: (value: any, other: any) => boolean,
): AppParametricSelector<P, R>;
export function wrapInCustomEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  customIsEqual: (value: any, other: any) => boolean,
): AppParametricSelector<P, R> {
  return createSelectorCreator(defaultMemoize, customIsEqual)(selector, (value) => value);
}

/** Simpler createStructuredSelector. */
export function createStructuredSelector<P, T>(selectors: {[K in keyof T]: AppParametricSelector<P, T[K]>}) {
  return reselectCreateStructuredSelector<AppState, P, T>(selectors);
}

/** Wrap the array output of a selector into a set. */
export function wrapInArrayToSetSelector<P, R>(getArray: AppParametricSelector<P, ReadonlyArray<R>>) {
  return createSelector(getArray, (array) => new Set(array));
}

/** Allow memoization selectors mapped from an array. */
export function createDistributedSelector<P, TMapped extends string | number, TResult>(
  selector: ParametricSelector<AppState, P, ReadonlyArray<TMapped>>,
  selectorCreator: (value: TMapped) => ParametricSelector<AppState, P, TResult>,
) {
  // Cache the previously created selectors (by mapped value).
  let cachedSelectors: {[K: string]: ParametricSelector<AppState, P, TResult> | undefined} = {};

  // Memoize the selectors for a given array of values.
  const getSelectors = createSelector(wrapInDeepEqualSelector(selector), (values) => {
    const selectors = values.map((value) => {
      const cachedSelector = cachedSelectors[value.toString()];
      return {
        value,
        selector: cachedSelector || selectorCreator(value),
      };
    });

    cachedSelectors = _(selectors)
      .keyBy((s) => s.value)
      .mapValues((s) => s.selector)
      .value();
    return selectors.map((s) => s.selector);
  });

  // Always re-evaluate every single selector.
  return (state: AppState, props: P) => {
    const selectors = getSelectors(state, props);
    return selectors.map((s) => s(state, props));
  };
}

/** Saner MapStateToProps + CreateStructureSelector. */
export function makeMapStateToProps<P = {}>() {
  return function mstpMaker<T>(makeSelectors: () => {[K in keyof T]: AppParametricSelector<P, T[K]>}) {
    return () => reselectCreateStructuredSelector<AppState, P, T>(makeSelectors());
  };
}

type AnonymousFunction = (...args: any) => any;
type UnwrappedThunkAction<T> = T extends ThunkAction<infer R> ? R : T;
export type UnwrappedActionCreator<T extends AnonymousFunction> = (
  ...args: Parameters<T>
) => UnwrappedThunkAction<ReturnType<T>>;

export function makeMapDispatchToProps<P = {}>() {
  return function mdtpMaker<T extends {[K: string]: AnonymousFunction}>(makeActionCreators: (props: P) => T) {
    return (dispatch: Dispatch, props: P): {[K in keyof T]: UnwrappedActionCreator<T[K]>} => {
      const actionCreators = makeActionCreators(props);
      return mapValues(
        actionCreators,
        (value) =>
          (...args: any[]) =>
            dispatch(value(...args)),
      );
    };
  };
}

/** Find the resulting state props from a MapStateToProps method. */
export type ResolveStateProps<TSelector extends () => ParametricSelector<any, any, any>> = ReturnType<
  ReturnType<TSelector>
>;

/*
 * Helpers.
 */

const hasOwn = Object.prototype.hasOwnProperty;

/* eslint no-self-compare: 0 */
function is(x: any, y: any) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  }

  return x !== x && y !== y;
}

/** Default equality check. */
export function isStrictEqual(a: any, b: any) {
  return a === b;
}

/**
 * ShallowEqual implementation similar to Redux'.
 * Based off https://github.com/reduxjs/react-redux/blob/master/src/utils/shallowEqual.js
 */
export function isShallowEqual(objA: any, objB: any) {
  if (is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (let i = 0; i < keysA.length; i++) {
    if (!hasOwn.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
}

// Adapted from reselect's areArgumentsShallowlyEqual, but skips the first argument.
function areTailArgumentsShallowlyEqual(
  equalityCheck: (a: any, b: any) => boolean,
  prev: IArguments,
  next: IArguments,
) {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const {length} = prev;
  for (let i = 1; i < length; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

// Adapted from reselect's defaultMemoize, but uses areTailArgumentsShallowlyEqual.
/* eslint-disable prefer-rest-params */
// eslint-disable-next-line @typescript-eslint/ban-types
function tailMemoize<F extends Function>(func: F, equalityCheck = isStrictEqual): F {
  let lastArgs: IArguments | null = null;
  let lastResult: any;

  // Reference arguments instead of spreading them for performance reasons.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return function tailMemoizedFunc() {
    // Apply arguments instead of spreading for performance.
    const result =
      !lastArgs || !areTailArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)
        ? func(...arguments)
        : lastResult;

    lastArgs = arguments;
    lastResult = result;
    return result;
  } as any;
}
/* eslint-enable prefer-rest-params */
